import { createFileRoute, Link } from '@tanstack/react-router'
import {
  Filter,
  ChevronDown,
  User,
  Users,
  Clock,
  AlertCircle,
  Play,
  GitBranch,
  Code,
  Server,
  ArrowUpDown,
  Layers,
  Calendar,
  AlertTriangle,
  List,
  Workflow,
} from 'lucide-react'
import React, { useState, useEffect, useRef } from 'react'

import { BatchOperationBar, TaskCheckbox } from '@/components/pm/workflow/batch-operation-bar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useMyWorkflowTasks,
  useMyTrackTasks,
  getStageDisplayName,
  getTrackDisplayName,
  getTrackStatusDisplayName,
  useApproveWorkflow,
  type MyWorkflowTasksParams,
  type MyTrackTasksParams,

  useEnhancedTasks,
  useGroupedTasks,
  GROUP_BY_OPTIONS,
  SORT_BY_OPTIONS,
  QUICK_FILTER_OPTIONS,
  formatDeadlineCountdown,
  formatWaitingTime,
  type EnhancedTasksParams,
  type GroupByOption,
  type SortByOption,
  type QuickFilter,
  type EnhancedTaskItem,
  type GroupedTasks, useViewPreferences, useSaveViewPreferences, type ViewPreferences
} from '@/hooks'
import { useBatchSelection } from '@/hooks'
import { cn } from '@/lib/utils'

import type { WorkflowStage, TrackType, TrackStatus } from '@company/shared'

export const Route = createFileRoute('/_authenticated/pm/my-workflow-tasks')({
  component: MyWorkflowTasksPage,
})

/**
 * 阶段筛选选项
 */
const STAGE_OPTIONS: { value: WorkflowStage | ''; label: string }[] = [
  { value: '', label: '全部阶段' },
  { value: 'product_discussion', label: '产品研讨' },
  { value: 'ui_design', label: 'UI设计' },
  { value: 'ui_confirmation', label: 'UI确认' },
  { value: 'frontend_development', label: '前端开发' },
  { value: 'backend_development', label: '后端开发' },
  { value: 'code_review', label: '代码审查' },
  { value: 'testing', label: '测试' },
  { value: 'product_acceptance', label: '产品验收' },
]

/**
 * 轨道筛选选项
 */
const TRACK_OPTIONS: { value: TrackType | ''; label: string }[] = [
  { value: '', label: '全部轨道' },
  { value: 'frontend', label: '前端' },
  { value: 'backend', label: '后端' },
]

/**
 * 轨道状态筛选选项
 */
const TRACK_STATUS_OPTIONS: { value: TrackStatus | ''; label: string }[] = [
  { value: '', label: '全部状态' },
  { value: 'pending', label: '等待开始' },
  { value: 'in_progress', label: '开发中' },
  { value: 'in_review', label: '审查中' },
  { value: 'approved', label: '已通过' },
]

/**
 * 视图模式
 */
type ViewMode = 'enhanced' | 'stage' | 'track'

/**
 * 我的工作流任务页面
 * Requirements: 6.1, 6.2, 6.5, 6.6, 6.7
 */
function MyWorkflowTasksPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('enhanced')

  // 增强视图参数
  const [enhancedParams, setEnhancedParams] = useState<EnhancedTasksParams>({
    groupBy: 'project',
    sortBy: 'deadline',
    sortOrder: 'asc',
    filter: 'all',
    page: 1,
    pageSize: 20,
  })

  // 阶段视图参数
  const [stageFilters, setStageFilters] = useState<MyWorkflowTasksParams>({
    role: 'all',
    page: 1,
    pageSize: 20,
  })

  // 轨道视图参数
  const [trackFilters, setTrackFilters] = useState<MyTrackTasksParams>({
    role: 'all',
    page: 1,
    pageSize: 20,
  })

  const [showFilters, setShowFilters] = useState(false)

  // 加载用户偏好
  const { data: savedPreferences } = useViewPreferences('workflow_tasks')
  const savePreferences = useSaveViewPreferences()

  // 应用保存的偏好
  useEffect(() => {
    if (savedPreferences?.preferences) {
      const prefs = savedPreferences.preferences
      setEnhancedParams((prev) => ({
        ...prev,
        groupBy: (prefs.groupBy as GroupByOption) || prev.groupBy,
        sortBy: (prefs.sortBy as SortByOption) || prev.sortBy,
        sortOrder: prefs.sortOrder || prev.sortOrder,
        filter: (prefs.defaultFilter as QuickFilter) || prev.filter,
        pageSize: prefs.pageSize || prev.pageSize,
      }))
    }
  }, [savedPreferences])

  // 保存偏好 - 使用 ref 来避免 useEffect 依赖问题
  const savePreferencesRef = useRef(savePreferences)
  savePreferencesRef.current = savePreferences

  // 当参数变化时自动保存偏好（防抖 1000ms）
  useEffect(() => {
    // 只在增强视图模式下保存
    if (viewMode !== 'enhanced') { return }

    // 延迟保存，避免频繁请求
    const timer = setTimeout(() => {
      const preferences: ViewPreferences = {
        groupBy: enhancedParams.groupBy,
        sortBy: enhancedParams.sortBy,
        sortOrder: enhancedParams.sortOrder,
        defaultFilter: enhancedParams.filter,
        pageSize: enhancedParams.pageSize,
      }
      savePreferencesRef.current.mutate({ viewType: 'workflow_tasks', preferences })
    }, 1000)
    return () => clearTimeout(timer)
  }, [enhancedParams.groupBy, enhancedParams.sortBy, enhancedParams.sortOrder, enhancedParams.filter, enhancedParams.pageSize, viewMode])

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">我的任务</h1>
          <p className="text-muted-foreground">查看我作为阶段负责人或参与人员的任务</p>
        </div>
        {/* 视图切换按钮 */}
        <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-1">
          <Link
            to="/pm/my-tasks"
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <List className="h-4 w-4" />
            列表视图
          </Link>
          <button
            className="inline-flex items-center gap-2 rounded-md bg-background px-4 py-2 text-sm font-medium shadow-sm"
          >
            <Workflow className="h-4 w-4" />
            工作流视图
          </button>
        </div>
      </div>

      {/* 视图切换 */}
      <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-1 w-fit">
        <button
          onClick={() => setViewMode('enhanced')}
          className={cn(
            'inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
            viewMode === 'enhanced'
              ? 'bg-background shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <Layers className="h-4 w-4" />
          增强视图
        </button>
        <button
          onClick={() => setViewMode('track')}
          className={cn(
            'inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
            viewMode === 'track'
              ? 'bg-background shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <GitBranch className="h-4 w-4" />
          轨道视图
        </button>
        <button
          onClick={() => setViewMode('stage')}
          className={cn(
            'inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
            viewMode === 'stage'
              ? 'bg-background shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <Play className="h-4 w-4" />
          阶段视图
        </button>
      </div>

      {viewMode === 'enhanced' ? (
        <EnhancedTasksView
          params={enhancedParams}
          setParams={setEnhancedParams}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
        />
      ) : viewMode === 'track' ? (
        <TrackTasksView
          filters={trackFilters}
          setFilters={setTrackFilters}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
        />
      ) : (
        <StageTasksView
          filters={stageFilters}
          setFilters={setStageFilters}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
        />
      )}
    </div>
  )
}


/**
 * 增强任务视图
 * Requirements: 6.1, 6.2, 6.5, 6.6
 */
function EnhancedTasksView({
  params,
  setParams,
  showFilters,
  setShowFilters,
}: {
  params: EnhancedTasksParams
  setParams: React.Dispatch<React.SetStateAction<EnhancedTasksParams>>
  showFilters: boolean
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>
}) {
  // 使用分组视图
  const { data: groupedData, isLoading: isGroupedLoading, error: groupedError } = useGroupedTasks(params)
  const { data: listData, isLoading: isListLoading, error: listError } = useEnhancedTasks(params)

  const isLoading = isGroupedLoading || isListLoading
  const error = groupedError || listError

  // 批量选择
  const allTasks = listData?.data ?? []
  const selection = useBatchSelection(allTasks, (task) => task.issueId)

  // 批量审批
  const approveWorkflow = useApproveWorkflow()
  const [isApproving, setIsApproving] = useState(false)

  const handleBatchApprove = async () => {
    if (selection.selectedCount === 0) { return }

    setIsApproving(true)
    try {
      // 逐个审批选中的任务
      for (const issueId of selection.selectedIdsArray) {
        await approveWorkflow.mutateAsync(issueId)
      }
      selection.clearSelection()
    } catch (error) {
      console.error('批量审批失败:', error)
    } finally {
      setIsApproving(false)
    }
  }

  // 统计数据
  const groups = groupedData ?? []
  const totalTasks = groups.reduce((sum, g) => sum + g.totalCount, 0)
  const overdueCount = groups.reduce((sum, g) => sum + g.overdueCount, 0)
  const requiresActionCount = allTasks.filter((t) => t.requiresAction).length

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  return (
    <>
      {/* 快捷筛选 */}
      <div className="flex flex-wrap items-center gap-2">
        {QUICK_FILTER_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => setParams((prev) => ({ ...prev, filter: option.value, page: 1 }))}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
              params.filter === option.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/80',
              option.color && params.filter !== option.value && option.color
            )}
          >
            {option.value === 'overdue' && <AlertCircle className="h-3.5 w-3.5" />}
            {option.value === 'dueToday' && <Calendar className="h-3.5 w-3.5" />}
            {option.value === 'dueThisWeek' && <Clock className="h-3.5 w-3.5" />}
            {option.value === 'atRisk' && <AlertTriangle className="h-3.5 w-3.5" />}
            {option.label}
            {option.value === 'overdue' && overdueCount > 0 && (
              <span className="ml-1 rounded-full bg-red-100 px-1.5 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-400">
                {overdueCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* 统计卡片 */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Layers className="h-4 w-4" />
            全部任务
          </div>
          <div className="mt-2 text-2xl font-bold">{totalTasks}</div>
        </div>

        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4 text-red-500" />
            已超时
          </div>
          <div className="mt-2 text-2xl font-bold text-red-600">{overdueCount}</div>
        </div>

        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            需要操作
          </div>
          <div className="mt-2 text-2xl font-bold text-amber-600">{requiresActionCount}</div>
        </div>

        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Layers className="h-4 w-4" />
            分组数
          </div>
          <div className="mt-2 text-2xl font-bold">{groups.length}</div>
        </div>
      </div>

      {/* 筛选和排序 */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* 分组选项 */}
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <Select
              value={params.groupBy || 'project'}
              onValueChange={(value) =>
                setParams((prev) => ({ ...prev, groupBy: value as GroupByOption, page: 1 }))
              }
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="分组方式" />
              </SelectTrigger>
              <SelectContent>
                {GROUP_BY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 排序选项 */}
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
            <Select
              value={params.sortBy || 'deadline'}
              onValueChange={(value) =>
                setParams((prev) => ({ ...prev, sortBy: value as SortByOption, page: 1 }))
              }
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="排序方式" />
              </SelectTrigger>
              <SelectContent>
                {SORT_BY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              onClick={() =>
                setParams((prev) => ({
                  ...prev,
                  sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
                }))
              }
              className="rounded-md border p-2 hover:bg-muted"
              title={params.sortOrder === 'asc' ? '升序' : '降序'}
            >
              <ArrowUpDown
                className={cn(
                  'h-4 w-4 transition-transform',
                  params.sortOrder === 'desc' && 'rotate-180'
                )}
              />
            </button>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
              showFilters ? 'bg-muted' : 'hover:bg-muted'
            )}
          >
            <Filter className="h-4 w-4" />
            更多筛选
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', showFilters && 'rotate-180')}
            />
          </button>
        </div>
      </div>

      {/* 批量操作栏 */}
      <BatchOperationBar
        selectedCount={selection.selectedCount}
        totalCount={allTasks.length}
        isAllSelected={selection.isAllSelected}
        isPartiallySelected={selection.isPartiallySelected}
        onSelectAll={selection.selectAll}
        onClearSelection={selection.clearSelection}
        onBatchApprove={() => void handleBatchApprove()}
        isApproving={isApproving}
        canApprove={selection.selectedItems.some((t) => t.requiresAction)}
      />

      {/* 任务列表 */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : groups.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <div className="text-center">
            <p className="text-lg font-medium">暂无任务</p>
            <p className="text-sm text-muted-foreground">当前没有需要你处理的工作流任务</p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <TaskGroup
              key={group.groupKey}
              group={group}
              selection={selection}
              groupBy={params.groupBy || 'project'}
            />
          ))}
        </div>
      )}
    </>
  )
}


/**
 * 任务分组组件
 */
function TaskGroup({
  group,
  selection,
  groupBy,
}: {
  group: GroupedTasks
  selection: ReturnType<typeof useBatchSelection<EnhancedTaskItem, string>>
  groupBy: GroupByOption
}) {
  const getGroupIcon = () => {
    switch (groupBy) {
      case 'project':
        return <Layers className="h-5 w-5 text-blue-500" />
      case 'stage':
        return <Play className="h-5 w-5 text-green-500" />
      case 'track':
        return <GitBranch className="h-5 w-5 text-purple-500" />
    }
  }

  return (
    <div className="rounded-lg border">
      <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-3">
        {getGroupIcon()}
        <h3 className="font-medium">{group.groupName}</h3>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{group.totalCount}</span>
        {group.overdueCount > 0 && (
          <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-400">
            {group.overdueCount} 超时
          </span>
        )}
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b bg-muted/20">
            <th className="w-10 px-4 py-2">
              <TaskCheckbox
                checked={group.tasks.every((t) => selection.isSelected(t.issueId))}
                onChange={(checked) => {
                  if (checked) {
                    selection.selectMany(group.tasks.map((t) => t.issueId))
                  } else {
                    selection.deselectMany(group.tasks.map((t) => t.issueId))
                  }
                }}
              />
            </th>
            <th className="px-4 py-2 text-left text-sm font-medium">角色</th>
            <th className="px-4 py-2 text-left text-sm font-medium">任务</th>
            {groupBy !== 'project' && (
              <th className="px-4 py-2 text-left text-sm font-medium">项目</th>
            )}
            {groupBy !== 'stage' && (
              <th className="px-4 py-2 text-left text-sm font-medium">阶段</th>
            )}
            <th className="px-4 py-2 text-left text-sm font-medium">截止日期</th>
            <th className="px-4 py-2 text-left text-sm font-medium">等待时间</th>
            <th className="px-4 py-2 text-left text-sm font-medium">状态</th>
            <th className="px-4 py-2 text-left text-sm font-medium">操作</th>
          </tr>
        </thead>
        <tbody>
          {group.tasks.map((task) => (
            <EnhancedTaskRow
              key={task.issueId}
              task={task}
              isSelected={selection.isSelected(task.issueId)}
              onToggle={() => selection.toggle(task.issueId)}
              groupBy={groupBy}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * 增强任务行组件
 */
function EnhancedTaskRow({
  task,
  isSelected,
  onToggle,
  groupBy,
}: {
  task: EnhancedTaskItem
  isSelected: boolean
  onToggle: () => void
  groupBy: GroupByOption
}) {
  const getTimeStatusColor = (status: string) => {
    switch (status) {
      case 'overdue':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
      case 'approaching_deadline':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
      default:
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600'
      case 'high':
        return 'text-orange-600'
      case 'medium':
        return 'text-blue-600'
      default:
        return 'text-gray-600'
    }
  }

  return (
    <tr
      className={cn(
        'border-b last:border-0 hover:bg-muted/30',
        task.requiresAction && 'bg-amber-50/50 dark:bg-amber-900/10',
        isSelected && 'bg-primary/5'
      )}
    >
      <td className="px-4 py-3">
        <TaskCheckbox checked={isSelected} onChange={onToggle} />
      </td>
      <td className="px-4 py-3">
        {task.role === 'owner' ? (
          <span className="inline-flex items-center gap-1 text-sm" title="负责人">
            <User className="h-4 w-4 text-primary" />
          </span>
        ) : task.role === 'assignee' ? (
          <span className="inline-flex items-center gap-1 text-sm" title="开发人员">
            <Code className="h-4 w-4 text-blue-500" />
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-sm" title="参与人员">
            <Users className="h-4 w-4 text-muted-foreground" />
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <Link
          to="/pm/issues/$issueId"
          params={{ issueId: task.issueId }}
          className="font-medium hover:text-primary hover:underline"
        >
          {task.issueTitle}
        </Link>
        <span className={cn('ml-2 text-xs', getPriorityColor(task.priority))}>
          [{task.priority === 'urgent' ? '紧急' : task.priority === 'high' ? '高' : task.priority === 'medium' ? '中' : '低'}]
        </span>
      </td>
      {groupBy !== 'project' && (
        <td className="px-4 py-3 text-sm text-muted-foreground">{task.projectName}</td>
      )}
      {groupBy !== 'stage' && (
        <td className="px-4 py-3">
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            <Play className="h-3 w-3" />
            {getStageDisplayName(task.currentStage as WorkflowStage)}
          </span>
        </td>
      )}
      <td className="px-4 py-3">
        <span
          className={cn(
            'inline-flex items-center gap-1 text-sm',
            task.timeStatus === 'overdue' && 'text-red-600',
            task.timeStatus === 'approaching_deadline' && 'text-amber-600'
          )}
        >
          <Calendar className="h-3.5 w-3.5" />
          {formatDeadlineCountdown(task.deadlineCountdown)}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          {formatWaitingTime(task.waitingTime)}
        </span>
      </td>
      <td className="px-4 py-3">
        {task.requiresAction ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            <AlertCircle className="h-3 w-3" />
            需要操作
          </span>
        ) : (
          <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', getTimeStatusColor(task.timeStatus))}>
            {task.timeStatus === 'overdue' ? '已超时' : task.timeStatus === 'approaching_deadline' ? '即将到期' : '正常'}
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <Link
          to="/pm/issues/$issueId"
          params={{ issueId: task.issueId }}
          className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
        >
          查看详情
        </Link>
      </td>
    </tr>
  )
}


/**
 * 轨道任务视图
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5
 */
function TrackTasksView({
  filters,
  setFilters,
  showFilters,
  setShowFilters,
}: {
  filters: MyTrackTasksParams
  setFilters: React.Dispatch<React.SetStateAction<MyTrackTasksParams>>
  showFilters: boolean
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { data: response, isLoading, error } = useMyTrackTasks(filters)

  const tasks = response?.data ?? []
  const meta = response?.meta

  // 按轨道分组
  const frontendTasks = tasks.filter((t) => t.track === 'frontend')
  const backendTasks = tasks.filter((t) => t.track === 'backend')

  // 统计数据
  const ownerCount = tasks.filter((t) => t.role === 'owner').length
  const assigneeCount = tasks.filter((t) => t.role === 'assignee').length
  const requiresActionCount = tasks.filter((t) => t.requiresAction).length

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  return (
    <>
      {/* 统计卡片 */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <button
          onClick={() => setFilters((prev) => ({ ...prev, role: 'all', page: 1 }))}
          className={cn(
            'rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
            filters.role === 'all' && 'border-primary bg-primary/5'
          )}
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <GitBranch className="h-4 w-4" />
            全部轨道任务
          </div>
          <div className="mt-2 text-2xl font-bold">{meta?.total ?? 0}</div>
        </button>

        <button
          onClick={() => setFilters((prev) => ({ ...prev, role: 'owner', page: 1 }))}
          className={cn(
            'rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
            filters.role === 'owner' && 'border-primary bg-primary/5'
          )}
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-4 w-4" />
            我负责审查的
          </div>
          <div className="mt-2 text-2xl font-bold">{ownerCount}</div>
        </button>

        <button
          onClick={() => setFilters((prev) => ({ ...prev, role: 'assignee', page: 1 }))}
          className={cn(
            'rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
            filters.role === 'assignee' && 'border-primary bg-primary/5'
          )}
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            我开发的
          </div>
          <div className="mt-2 text-2xl font-bold">{assigneeCount}</div>
        </button>

        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            需要操作
          </div>
          <div className="mt-2 text-2xl font-bold text-amber-600">{requiresActionCount}</div>
        </div>
      </div>

      {/* 筛选 */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
              showFilters ? 'bg-muted' : 'hover:bg-muted'
            )}
          >
            <Filter className="h-4 w-4" />
            筛选
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', showFilters && 'rotate-180')}
            />
          </button>
        </div>

        {showFilters && (
          <div className="rounded-lg border bg-card p-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-2 block text-sm font-medium">轨道</label>
                <Select
                  value={filters.track || ''}
                  onValueChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      track: (value as TrackType) || undefined,
                      page: 1,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部轨道" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRACK_OPTIONS.map((option) => (
                      <SelectItem key={option.value || 'all'} value={option.value || 'all'}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">状态</label>
                <Select
                  value={filters.status || ''}
                  onValueChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      status: (value as TrackStatus) || undefined,
                      page: 1,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部状态" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRACK_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value || 'all'} value={option.value || 'all'}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 任务列表 */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : tasks.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <div className="text-center">
            <p className="text-lg font-medium">暂无轨道任务</p>
            <p className="text-sm text-muted-foreground">当前没有需要你处理的轨道任务</p>
          </div>
        </div>
      ) : (
        <>
          {/* 按轨道分组显示 */}
          <div className="space-y-6">
            {/* 前端轨道 */}
            {frontendTasks.length > 0 && (
              <TrackTaskGroup
                track="frontend"
                tasks={frontendTasks}
                icon={<Code className="h-5 w-5 text-blue-500" />}
              />
            )}

            {/* 后端轨道 */}
            {backendTasks.length > 0 && (
              <TrackTaskGroup
                track="backend"
                tasks={backendTasks}
                icon={<Server className="h-5 w-5 text-green-500" />}
              />
            )}
          </div>

          {/* 分页 */}
          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                显示 {(meta.page - 1) * meta.pageSize + 1}-
                {Math.min(meta.page * meta.pageSize, meta.total)} / 共 {meta.total} 条
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
                  disabled={meta.page <= 1}
                  className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                >
                  上一页
                </button>
                <span className="text-sm">
                  {meta.page} / {meta.totalPages}
                </span>
                <button
                  onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
                  disabled={meta.page >= meta.totalPages}
                  className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}

/**
 * 轨道任务分组
 */
function TrackTaskGroup({
  track,
  tasks,
  icon,
}: {
  track: TrackType
  tasks: Array<{
    issueId: string
    issueTitle: string
    projectId: string
    projectName: string
    track: TrackType
    trackStatus: TrackStatus
    role: 'owner' | 'assignee'
    assigneeId: string | null
    requiresAction: boolean
    waitingTime: number
  }>
  icon: React.ReactNode
}) {
  return (
    <div className="rounded-lg border">
      <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-3">
        {icon}
        <h3 className="font-medium">{getTrackDisplayName(track)}轨道</h3>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{tasks.length}</span>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b bg-muted/20">
            <th className="px-4 py-2 text-left text-sm font-medium">角色</th>
            <th className="px-4 py-2 text-left text-sm font-medium">任务</th>
            <th className="px-4 py-2 text-left text-sm font-medium">项目</th>
            <th className="px-4 py-2 text-left text-sm font-medium">轨道状态</th>
            <th className="px-4 py-2 text-left text-sm font-medium">等待时间</th>
            <th className="px-4 py-2 text-left text-sm font-medium">状态</th>
            <th className="px-4 py-2 text-left text-sm font-medium">操作</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <tr
              key={`${task.issueId}-${task.track}`}
              className={cn(
                'border-b last:border-0 hover:bg-muted/30',
                task.requiresAction && 'bg-amber-50/50 dark:bg-amber-900/10'
              )}
            >
              <td className="px-4 py-3">
                {task.role === 'owner' ? (
                  <span className="inline-flex items-center gap-1 text-sm" title="轨道负责人">
                    <User className="h-4 w-4 text-primary" />
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-sm" title="开发人员">
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </span>
                )}
              </td>
              <td className="px-4 py-3">
                <Link
                  to="/pm/issues/$issueId"
                  params={{ issueId: task.issueId }}
                  className="font-medium hover:text-primary hover:underline"
                >
                  {task.issueTitle}
                </Link>
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">{task.projectName}</td>
              <td className="px-4 py-3">
                <TrackStatusBadge status={task.trackStatus} />
              </td>
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  {formatWaitingTime(task.waitingTime)}
                </span>
              </td>
              <td className="px-4 py-3">
                {task.requiresAction ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    <AlertCircle className="h-3 w-3" />
                    需要操作
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">等待中</span>
                )}
              </td>
              <td className="px-4 py-3">
                <Link
                  to="/pm/issues/$issueId"
                  params={{ issueId: task.issueId }}
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                >
                  查看详情
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * 轨道状态徽章
 */
function TrackStatusBadge({ status }: { status: TrackStatus }) {
  const getStatusColor = (s: TrackStatus) => {
    switch (s) {
      case 'pending':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
      case 'in_progress':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
      case 'in_review':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
      case 'approved':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      case 'skipped':
        return 'bg-muted text-muted-foreground'
    }
  }

  return (
    <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', getStatusColor(status))}>
      {getTrackStatusDisplayName(status)}
    </span>
  )
}


/**
 * 阶段任务视图（原有视图）
 * Requirements: 12.1, 12.2, 12.3, 12.4, 12.5
 */
function StageTasksView({
  filters,
  setFilters,
  showFilters,
  setShowFilters,
}: {
  filters: MyWorkflowTasksParams
  setFilters: React.Dispatch<React.SetStateAction<MyWorkflowTasksParams>>
  showFilters: boolean
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { data: response, isLoading, error } = useMyWorkflowTasks(filters)

  const tasks = response?.data ?? []
  const meta = response?.meta

  // 统计数据
  const ownerCount = tasks.filter((t) => t.role === 'owner').length
  const participantCount = tasks.filter((t) => t.role === 'participant').length
  const requiresActionCount = tasks.filter((t) => t.requiresAction).length

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  return (
    <>
      {/* 统计卡片 */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <button
          onClick={() => setFilters((prev) => ({ ...prev, role: 'all', page: 1 }))}
          className={cn(
            'rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
            filters.role === 'all' && 'border-primary bg-primary/5'
          )}
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            全部任务
          </div>
          <div className="mt-2 text-2xl font-bold">{meta?.total ?? 0}</div>
        </button>

        <button
          onClick={() => setFilters((prev) => ({ ...prev, role: 'owner', page: 1 }))}
          className={cn(
            'rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
            filters.role === 'owner' && 'border-primary bg-primary/5'
          )}
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-4 w-4" />
            我负责的
          </div>
          <div className="mt-2 text-2xl font-bold">{ownerCount}</div>
        </button>

        <button
          onClick={() => setFilters((prev) => ({ ...prev, role: 'participant', page: 1 }))}
          className={cn(
            'rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
            filters.role === 'participant' && 'border-primary bg-primary/5'
          )}
        >
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            我参与的
          </div>
          <div className="mt-2 text-2xl font-bold">{participantCount}</div>
        </button>

        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            需要操作
          </div>
          <div className="mt-2 text-2xl font-bold text-amber-600">{requiresActionCount}</div>
        </div>
      </div>

      {/* 筛选 */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
              showFilters ? 'bg-muted' : 'hover:bg-muted'
            )}
          >
            <Filter className="h-4 w-4" />
            筛选
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', showFilters && 'rotate-180')}
            />
          </button>
        </div>

        {showFilters && (
          <div className="rounded-lg border bg-card p-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-2 block text-sm font-medium">阶段</label>
                <Select
                  value={filters.stage || ''}
                  onValueChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      stage: (value as WorkflowStage) || undefined,
                      page: 1,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全部阶段" />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGE_OPTIONS.map((option) => (
                      <SelectItem key={option.value || 'all'} value={option.value || 'all'}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 任务列表 */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : tasks.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <div className="text-center">
            <p className="text-lg font-medium">暂无工作流任务</p>
            <p className="text-sm text-muted-foreground">当前没有需要你处理的工作流任务</p>
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-lg border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left text-sm font-medium">角色</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">任务</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">项目</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">当前阶段</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">等待时间</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">状态</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr
                    key={task.issueId}
                    className={cn(
                      'border-b last:border-0 hover:bg-muted/30',
                      task.requiresAction && 'bg-amber-50/50 dark:bg-amber-900/10'
                    )}
                  >
                    <td className="px-4 py-3">
                      {task.role === 'owner' ? (
                        <span className="inline-flex items-center gap-1 text-sm" title="阶段负责人">
                          <User className="h-4 w-4 text-primary" />
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-sm" title="参与人员">
                          <Users className="h-4 w-4 text-muted-foreground" />
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to="/pm/issues/$issueId"
                        params={{ issueId: task.issueId }}
                        className="font-medium hover:text-primary hover:underline"
                      >
                        {task.issueTitle}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {task.projectName}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        <Play className="h-3 w-3" />
                        {getStageDisplayName(task.currentStage as WorkflowStage)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        {formatWaitingTime(task.waitingTime)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {task.requiresAction ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                          <AlertCircle className="h-3 w-3" />
                          需要操作
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">等待中</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to="/pm/issues/$issueId"
                        params={{ issueId: task.issueId }}
                        className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                      >
                        查看详情
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 分页 */}
          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                显示 {(meta.page - 1) * meta.pageSize + 1}-
                {Math.min(meta.page * meta.pageSize, meta.total)} / 共 {meta.total} 条
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
                  disabled={meta.page <= 1}
                  className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                >
                  上一页
                </button>
                <span className="text-sm">
                  {meta.page} / {meta.totalPages}
                </span>
                <button
                  onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
                  disabled={meta.page >= meta.totalPages}
                  className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}
